import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalConfigContext } from 'GlobalConfig.context';
import { useMemo } from 'preact/hooks';
import Shipment from 'interfaces/PmParcel';
import { userAuthService } from 'services/userAuth.services';
import useSearchForm from 'hooks/useSearchForm';
import { ptpCatchError } from 'services/sentry.services';
import { ErrorCodes } from 'constants/errors.constants';
import { isRuntimeError } from 'utils/fetcher.utils';
import { validateConsumer } from '../services';

interface Error {
  general?: string;
  [key: string]: string;
}

const useConsumerValidationModal = (
  shipment: Shipment,
  onValidateSucceed: (shipment: Shipment) => void,
) => {
  const { t } = useTranslation();
  const { organizationId, cmsSlug, config } = useGlobalConfigContext();
  const additionalValidation = config.additionalIdReferencesBox;
  const { poId } = shipment;

  const searchFields = config.searchingIdReferencesBox;
  const tabs = useMemo(
    () =>
      additionalValidation.fields.map((field) => {
        const found = searchFields.find((item) => item.key === field);
        return { key: found.key, label: t(found.name) };
      }),
    [additionalValidation.fields, searchFields, t],
  );

  const [errors, setErrors] = useState<Error>({});
  const [onValidating, setOnValidating] = useState(false);

  const { formFields, initSearchForm, onChangeSearchForm, onSubmitSearchForm } =
    useSearchForm();

  const onChangeTab = (index: number) => {
    if (tabs.length > 0) {
      initSearchForm(tabs[index].key);
    }
  };

  useEffect(() => {
    if (tabs.length > 0) {
      const defaultTab = tabs[0].key;
      initSearchForm(defaultTab);
    }
  }, [tabs]);

  const onSubmitForm = async (event: Event) => {
    onSubmitSearchForm(async (searchTerms) => {
      try {
        setOnValidating(true);
        const { token, shipment } = await validateConsumer(
          organizationId,
          cmsSlug,
          poId,
          searchTerms,
        );
        userAuthService.setValidateUserToken(token);
        onValidateSucceed(shipment);
      } catch (error) {
        if (isRuntimeError(error)) {
          ptpCatchError(error);
          setErrors((prevErrors) => ({
            ...prevErrors,
            general: t(ErrorCodes.GeneralError),
          }));
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          general: t(error.message),
        }));
      } finally {
        setOnValidating(false);
      }
    })(event);
  };

  return {
    // configs
    buttonText: t('BUTTON_SUBMIT'),
    modalTitle: t('RETURN_ENTER_YOUR_ID'),
    tabs,
    shouldShowTabs: tabs.length > 1,

    // state
    formFields,
    onValidating,

    // handler
    onChangeTab,
    onChangeForm: onChangeSearchForm,
    onSubmitForm,

    errors,
  };
};

export default useConsumerValidationModal;
